import {
  Audio,
  AVPlaybackStatusError,
  AVPlaybackStatusSuccess,
  InterruptionModeAndroid,
} from 'expo-av';
import { captureException } from '@/utils/sentry';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSharedValue } from 'react-native-reanimated';

export const PROGRESS_UPDATE_INTERVAL = 250;

export type AudioPlayerState = ReturnType<typeof useAudioPlayer>;

export const useAudioPlayer = (source: string | null) => {
  const audioRef = useRef<Audio.Sound | null>(null);
  const [status, setStatus] = useState<AVPlaybackStatusSuccess | null>(null);
  const [error, setError] = useState<AVPlaybackStatusError | null>(null);

  // Track as ref to avoid re-creating callbacks that rely on current status
  const statusRef = useRef<AVPlaybackStatusSuccess | null>(null);
  const wasPlayingRef = useRef<boolean | null>(null);

  const progress = useSharedValue(0);
  const min = useSharedValue(0);
  const max = useSharedValue(1);

  useEffect(() => {
    const loadSound = async (sourceURI: string) => {
      try {
        await Audio.setAudioModeAsync({
          interruptionModeAndroid: InterruptionModeAndroid.DoNotMix,
          playsInSilentModeIOS: true,
          shouldDuckAndroid: true,
          staysActiveInBackground: false,
        });

        const { sound } = await Audio.Sound.createAsync(
          { uri: sourceURI },
          { progressUpdateIntervalMillis: PROGRESS_UPDATE_INTERVAL },
          (status) => {
            if (status.isLoaded) {
              setStatus(status);
              statusRef.current = status;
            } else {
              setError(status);
            }
          },
          true,
        );
        audioRef.current = sound;

        const initialStatus = await sound.getStatusAsync();
        if (initialStatus.isLoaded) {
          setStatus(initialStatus);
          statusRef.current = initialStatus;
        } else {
          setError(initialStatus);
        }
      } catch (error) {
        captureException(error);
      }
    };
    if (source) {
      loadSound(source);
    }

    return () => {
      const unloadAudio = async () => {
        await audioRef.current?.unloadAsync();
      };

      unloadAudio();
    };
  }, [source]);

  useEffect(() => {
    if (status?.durationMillis) {
      max.value = status.durationMillis;
    }
    // max is updated as part of this hook, so don't include in dep array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.durationMillis]);

  useEffect(() => {
    if (status?.positionMillis) {
      progress.value = status.positionMillis;
    }
    // progress is updated as part of this hook, so don't include in dep array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.positionMillis]);

  const onPlayPausePress = useCallback(async () => {
    const sound = audioRef.current;
    if (!sound) {
      return;
    }
    if (status?.isLoaded) {
      if (!status.isPlaying) {
        const isFinished = status.durationMillis === status.positionMillis;
        if (isFinished) {
          await sound.setPositionAsync(0);
        }
        await sound.playAsync();
      } else {
        await sound.pauseAsync();
      }
    }
  }, [status]);

  const onSlidingStart = useCallback(async () => {
    const sound = audioRef.current;
    if (!sound) {
      return;
    }
    // Track previous play/pause state to restore it after slider is released
    wasPlayingRef.current = statusRef.current?.isPlaying ?? false;
    if (wasPlayingRef.current) {
      await sound.pauseAsync();
    }
  }, []);

  const onSlidingComplete = useCallback(async (value: number) => {
    const sound = audioRef.current;
    if (!sound) {
      return;
    }
    try {
      await sound.setPositionAsync(value);
      if (wasPlayingRef.current) {
        await sound.playAsync();
      }
    } catch (error) {
      captureException(error);
    }
    wasPlayingRef.current = null;
  }, []);

  const setPosition = useCallback(async (value: number) => {
    const sound = audioRef.current;
    if (!sound) {
      return;
    }
    await sound.setPositionAsync(value);
  }, []);

  return useMemo(
    () => ({
      error,
      max,
      min,
      onPlayPausePress,
      onSlidingComplete,
      onSlidingStart,
      progress,
      setPosition,
      status,
    }),
    [
      error,
      max,
      min,
      onPlayPausePress,
      onSlidingComplete,
      onSlidingStart,
      progress,
      setPosition,
      status,
    ],
  );
};
